/**
 * Яндекс.Метрика.
 *
 * Номер счётчика приходит из окружения на сборке (`VITE_METRIKA_ID`). Пусто —
 * счётчика нет вовсе: в разработке и на стендах он только пачкает статистику
 * прода, а «случайно собрали стенд с боевым номером» — ошибка, которую потом
 * не отличить от настоящего трафика.
 *
 * Счётчик нужен не ради красивых графиков. Директ оптимизирует показы по
 * целям: без них доступны только ручные ставки и оплата за клики, то есть
 * платить придётся за трафик, а не за регистрации.
 */
import { rememberClientId } from './attribution.js';

const COUNTER_ID = Number(import.meta.env['VITE_METRIKA_ID'] ?? '') || null;

declare global {
  interface Window {
    ym?: ((id: number, action: string, ...args: unknown[]) => void) & { a?: unknown[]; l?: number };
  }
}

/** Цели. Имена совпадают с теми, что заводятся в интерфейсе Метрики. */
export type Goal =
  /** Аккаунт заведён. Основная цель рекламных кампаний. */
  | 'signup'
  /**
   * Документ собран: человек дошёл до ценности, а не только до формы.
   *
   * Не «первый»: браузер не знает, какой он по счёту. Уникальность считает сама
   * Метрика — в отчётах есть посетители, достигшие цели, а не только achievements.
   */
  | 'document_ready'
  /** Оплата прошла. По ней считается окупаемость кампании. */
  | 'payment'
  /**
   * Задача написана и отправлена с витрины — до регистрации.
   *
   * Отдельно от signup, потому что это разные вещи: между ними стоит вся
   * регистрация, и разрыв между целями и есть то, что она стоит. Без этой
   * цели «реклама приводит людей, которые уходят на форме» и «реклама
   * приводит не тех» выглядели бы одинаково.
   */
  | 'prompt_started'
  /**
   * Код на почту запрошен: письмо ушло, аккаунта ещё нет.
   *
   * Между «написал задачу» и «завёл аккаунт» стоит не один шаг, а два, и без
   * этой цели они складываются в один провал: непонятно, ушёл человек, не
   * оставив адрес, или оставил и не дождался письма. Чинится это по-разному,
   * поэтому и различать надо до того, как чинить.
   *
   * Отмечается только на переходе к вводу кода. Повторная отправка письма зовёт
   * тот же код, но второй раз цель не ставит: это то же самое намерение.
   */
  | 'login_code_requested'
  /**
   * Упор в потолок плана или в пустой баланс.
   *
   * Самая тёплая аудитория, какая бывает: человек хочет продолжить работу
   * прямо сейчас и не может. Для ретаргетинга это ценнее регистрации, а для
   * продукта — единственный способ увидеть, сколько людей уткнулись в границу
   * и ушли, вместо того чтобы заплатить.
   */
  | 'limit_reached'
  /**
   * Счёт выставлен.
   *
   * Не дубль payment, а замена ей там, где payment не работает. Оплату по
   * расчётному счёту браузер не увидит никогда: деньги приходят через день-два,
   * и подтверждает их сервер. Выставление счёта — единственный след такого
   * платежа на стороне клиента, и для обучения Директа он полезнее редкой
   * и заведомо неполной цели оплаты.
   */
  | 'invoice_created'
  /** План сменён. Переход вверх по линейке — то же намерение платить. */
  | 'plan_changed'
  /** Документ выгружен файлом: PDF, PowerPoint, картинками или архивом. */
  | 'export_done'
  /**
   * Ссылка на документ создана.
   *
   * Момент, ради которого продукт и покупают: документ уходит клиенту
   * заказчика. Дальше начинается чужая страница, где считать уже нечего.
   */
  | 'share_created';

export interface MetrikaOptions {
  /**
   * Вебвизор — запись сессий.
   *
   * Включается только на витрине. В кабинете запись означала бы попадание в
   * чужой сервис содержимого документов и полей, которые заполняет клиент, —
   * а мы этого не обещали ни в политике, ни человеку.
   *
   * С появлением поля задачи на главной у витрины возникло своё исключение:
   * туда вставляют бриф клиента, и это ровно тот текст, ради которого запись
   * запрещена в кабинете. Само поле закрыто в разметке классами
   * `ym-hide-content` и `ym-disable-keys` (`pages/landing/TaskField.vue`) —
   * проверяется это только глазами в записи, автоматической защиты тут нет.
   */
  webvisor?: boolean;
}

export function initMetrika(options: MetrikaOptions = {}): void {
  if (typeof window === 'undefined' || COUNTER_ID === null) return;
  // Второй вызов на той же странице удвоил бы просмотры.
  if (window.ym) return;

  /*
   * Очередь до загрузки скрипта: `ym(...)` можно звать сразу, вызовы
   * накопятся и уедут, когда tag.js приедет. Так цель, достигнутая в первую
   * секунду, не теряется.
   */
  const queue: ((id: number, action: string, ...args: unknown[]) => void) & {
    a?: unknown[];
    l?: number;
  } = function (...args: unknown[]) {
    (queue.a = queue.a ?? []).push(args);
  } as never;
  queue.l = Date.now();
  window.ym = queue;

  const script = document.createElement('script');
  script.async = true;
  // Номер в адресе скрипта — так его выдаёт сам сервис: по нему Метрика отдаёт
  // сборку под конкретный счётчик, а не общую.
  script.src = `https://mc.yandex.ru/metrika/tag.js?id=${COUNTER_ID}`;
  document.head.appendChild(script);

  window.ym(COUNTER_ID, 'init', {
    // Витрина приезжает готовой разметкой, а не собирается скриптом: без
    // этого признака Метрика считает такие страницы неверно.
    ssr: true,
    /*
     * Адрес и переход — явно, и это следствие ssr.
     *
     * С признаком выше Метрика не берёт их со страницы сама: разметку мог
     * отдать сервер под другим адресом, чем тот, что в строке браузера.
     * Промолчать значит получить просмотры без адреса и весь рекламный трафик
     * в «прямых заходах».
     */
    referrer: document.referrer,
    url: window.location.href,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: options.webvisor === true,
    // Кабинет — приложение с историей браузера: без этого переход между
    // разделами не считался бы просмотром.
    defer: false,
  });

  /*
   * Идентификатор браузера — сразу и в хранилище.
   *
   * Спросить его в момент регистрации не выйдет: ответ приходит колбэком, а
   * форма к тому времени уже отправлена. Поэтому снимаем на входе и держим
   * рядом с метками кампании — оттуда он и уедет на сервер.
   *
   * Зачем он нужен, подробно сказано у поля `metrikaClientId` в контракте:
   * без него оплату по счёту и заявку из документа отдать Метрике нечем.
   */
  window.ym(COUNTER_ID, 'getClientID', (clientId: unknown) => {
    if (typeof clientId === 'string' && clientId) rememberClientId(clientId);
  });
}

/**
 * Отметить достижение цели.
 *
 * Молчит, если счётчика нет: в разработке цели достигаются постоянно, и
 * падать из-за отсутствующего счётчика код не должен.
 */
export function reachGoal(goal: Goal, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || COUNTER_ID === null) return;
  window.ym?.(COUNTER_ID, 'reachGoal', goal, params);
}

/**
 * Связать визиты кабинета с пользователем.
 *
 * Без этого все визиты внутри кабинета обезличены, и сегмент вроде «платящие
 * организации» строить не из чего: Метрика видит браузеры, а не людей, и
 * человек с ноутбука и с телефона для неё двое разных.
 *
 * Уходит идентификатор из базы — не адрес почты и не имя. Метрика чужой
 * сервис, и персональные данные ей не обещаны ни в политике, ни человеку;
 * идентификатор же ничего о человеке не говорит никому, кроме нас.
 */
export function identify(userId: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || COUNTER_ID === null) return;

  window.ym?.(COUNTER_ID, 'setUserID', userId);
  if (params) window.ym?.(COUNTER_ID, 'userParams', params);
}

/**
 * Сообщить о переходе внутри приложения.
 *
 * В кабинете адрес меняет роутер, перезагрузки страницы не происходит, и сам
 * счётчик такой переход не заметит.
 */
export function trackPageView(path: string): void {
  if (typeof window === 'undefined' || COUNTER_ID === null) return;
  window.ym?.(COUNTER_ID, 'hit', path);
}
